import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import TimeEntryForm from './TimeEntryForm';

// "Log time" from anywhere in Support Coordination (PRODUCT.md principle 1: logging time is one step
// from anywhere). Any screen calls useLogTime()(opts) to open the one time-entry sheet:
//   open({ coordinationId?, participantName?, entry?, defaults?, budgets?, services?, onSaved? })
// Without a coordinationId the sheet asks for the participant first (yours first, then the ones you
// logged recently). Without budgets/services it loads the participant's own. After a save it calls
// the caller's onSaved and fires a window event 'sc:time-saved' ({ detail: entry }) so any list on
// screen (Notes & Billing, SC hours, Overview) can refresh itself.
const Ctx = createContext(null);

export function LogTimeProvider({ children, staff = [], canLogForOthers = false }) {
  const [state, setState] = useState(null);
  const open = useCallback((opts = {}) => setState({ ...opts, openedAt: Date.now() }), []);
  const close = useCallback(() => setState(null), []);
  const value = useMemo(() => ({ open, close }), [open, close]);
  const onSaved = (entry) => {
    try { window.dispatchEvent(new CustomEvent('sc:time-saved', { detail: entry || null })); } catch { /* old browsers */ }
    if (state && state.onSaved) state.onSaved(entry);
    if (!(state && state.entry && state.entry.locked)) setState(null);
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      <TimeEntryForm
        key={state ? state.openedAt : 'closed'}
        open={!!state}
        onClose={close}
        coordinationId={state?.coordinationId}
        participantName={state?.participantName}
        entry={state?.entry}
        defaults={state?.defaults}
        budgets={state?.budgets}
        services={state?.services}
        staff={staff}
        canLogForOthers={canLogForOthers}
        onSaved={onSaved}
      />
    </Ctx.Provider>
  );
}

// For a screen that needs Log time whether or not the page above it provides one: uses the page's
// provider when there is one, otherwise brings its own.
export function EnsureLogTime({ children, staff, canLogForOthers }) {
  const ctx = useContext(Ctx);
  if (ctx) return children;
  return <LogTimeProvider staff={staff} canLogForOthers={canLogForOthers}>{children}</LogTimeProvider>;
}

// Returns open(opts). Outside a provider it is a no-op, so a component can call it safely.
export function useLogTime() {
  const ctx = useContext(Ctx);
  return ctx ? ctx.open : NOOP;
}
const NOOP = () => {};

// Refresh a list when time is saved anywhere.
export function useOnTimeSaved(fn) {
  React.useEffect(() => {
    const h = (e) => fn(e.detail);
    window.addEventListener('sc:time-saved', h);
    return () => window.removeEventListener('sc:time-saved', h);
  }, [fn]);
}
